import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { money, monthLabel } from "../lib/format";
import { Panel, Empty } from "./Widgets";

export const COMPANIES = ["Paysafe", "Fiserv", "Priority Payments"];
const COLORS = { Paysafe: "#3B82F6", Fiserv: "#F97316", "Priority Payments": "#EC4899" };
export const color = (c) => COLORS[c] || "#A855F7";
const slug = (s) => s.replace(/\s+/g, "-");
export const CompanyOptions = () => <><option value="">All companies</option>{COMPANIES.map((c) => <option key={c} value={c}>{c}</option>)}</>;

export function CompanyCards({ d, payoutLabel = "Earned", paid, testPrefix = "company" }) {
  const list = d.companies || [];
  if (!list.length) return null;
  const tr = list.reduce((s, c) => s + c.residual, 0);
  const tp = list.reduce((s, c) => s + c.payout, 0);
  const yr = list.reduce((s, c) => s + (c.ytd_residual || 0), 0);
  const yp = list.reduce((s, c) => s + (c.ytd_payout || 0), 0);
  const year = (d.month || "").slice(0, 4);
  const ytd = (v, id) => <div className="mt-1 text-[11px] font-mono text-slate-400" data-testid={id}>YTD {year}: {money(v)}</div>;
  const carry = paid != null && Math.abs(paid - tp) >= 0.01 ? paid - tp : 0;
  const carryNote = carry ? <div className={`text-[11px] ${carry < 0 ? "text-rose-300" : "text-slate-400"}`} data-testid={`${testPrefix}-carry-note`}><span>{money(carry)}</span> carry · paid <span>{money(paid)}</span></div> : null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-testid={`${testPrefix}-cards`}>
      {list.map((c) => (
        <div key={c.company} data-testid={`${testPrefix}-card-${c.company}`} className="rounded-lg border border-slate-800 bg-[#131B2E] p-4 border-l-4" style={{ borderLeftColor: color(c.company) }}>
          <div className="text-[11px] font-mono uppercase tracking-wider" style={{ color: color(c.company) }}>{c.company} · {monthLabel(d.month)}</div>
          <div className="mt-3 grid grid-cols-1 xl:grid-cols-2 gap-3">
            <div><div className="text-[11px] text-slate-500">Residual</div>
              <div className="font-mono text-lg text-slate-100" data-testid={`${testPrefix}-residual-${c.company}`}>{money(c.residual)}</div>
              <div className="text-[11px] text-slate-500">{c.residual_pct}% of total</div>
              {ytd(c.ytd_residual || 0, `${testPrefix}-ytd-residual-${c.company}`)}</div>
            <div><div className="text-[11px] text-slate-500">{payoutLabel}</div>
              <div className="font-mono text-lg text-emerald-400" data-testid={`${testPrefix}-payout-${c.company}`}>{money(c.payout)}</div>
              <div className="text-[11px] text-slate-500">{c.payout_pct}% of total</div>
              {ytd(c.ytd_payout || 0, `${testPrefix}-ytd-payout-${c.company}`)}
              {list.length === 1 && carryNote}</div>
          </div>
        </div>
      ))}
      {list.length > 1 && (
        <div data-testid={`${testPrefix}-card-total`} className="rounded-lg border border-slate-700 bg-[#0d1320] p-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">All companies · 100%</div>
          <div className="mt-3 grid grid-cols-1 xl:grid-cols-2 gap-3">
            <div><div className="text-[11px] text-slate-500">Residual</div><div className="font-mono text-lg text-slate-100">{money(tr)}</div>{ytd(yr, `${testPrefix}-ytd-residual-total`)}</div>
            <div><div className="text-[11px] text-slate-500">{payoutLabel}</div><div className="font-mono text-lg text-emerald-400">{money(tp)}</div>{ytd(yp, `${testPrefix}-ytd-payout-total`)}{carryNote}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CompanySwitch({ value, onChange, names = COMPANIES, testId = "company-switch" }) {
  const opts = ["", ...names];
  return (
    <div className="inline-flex rounded-md border border-slate-700 overflow-hidden" data-testid={testId}>
      {opts.map((o) => (
        <button key={o || "all"} data-testid={`${testId}-${slug(o) || "all"}`} onClick={() => onChange(o)}
          className={`px-3 h-8 text-xs font-mono uppercase transition-colors ${value === o ? "bg-slate-200 text-slate-900" : "text-slate-400 hover:text-slate-100 bg-[#0B0F17]"}`}
          style={value === o && o ? { background: color(o), color: "#0B0F17" } : undefined}>
          {o || "All"}
        </button>
      ))}
    </div>
  );
}

export function AgentCompanyBars({ agents, names = COMPANIES }) {
  const data = (agents || []).filter((a) => Object.keys(a.by_company || {}).length)
    .map((a) => ({ name: a.name, ...Object.fromEntries(names.map((n) => [n, a.by_company[n] || 0])) }));
  return (
    <Panel title="Agent earnings by company">
      {!data.length ? <Empty>No agent earnings this month.</Empty> : (
        <div className="p-4 h-[280px]" data-testid="agent-company-split-chart">
          <ResponsiveContainer>
            <BarChart data={data} margin={{ left: 10, right: 10, top: 10 }}>
              <CartesianGrid stroke="#1E293B" vertical={false} />
              <XAxis dataKey="name" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} interval={0} />
              <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`} />
              <Tooltip cursor={{ fill: "#1E293B" }} labelStyle={{ color: "#F1F5F9" }} itemStyle={{ color: "#E2E8F0" }}
                contentStyle={{ background: "#0B0F17", border: "1px solid #334155", borderRadius: 6 }} formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
              {names.map((n) => <Bar key={n} dataKey={n} name={n} stackId="a" fill={color(n)} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}

export function CompanyBars({ d, metric = "residual", title, testId = "company-bars" }) {
  const names = d.company_names || [];
  const data = (d.company_trend || []).map((r) => ({ ...r, label: monthLabel(r.month) }));
  return (
    <Panel title={title}>
      {!data.length ? <Empty>No data yet.</Empty> : (
        <div className="p-4 h-[280px]" data-testid={testId}>
          <ResponsiveContainer>
            <BarChart data={data} margin={{ left: 10, right: 10, top: 10 }}>
              <CartesianGrid stroke="#1E293B" vertical={false} />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Math.round(v).toLocaleString()}`} />
              <Tooltip cursor={{ fill: "#1E293B" }} labelStyle={{ color: "#F1F5F9" }} itemStyle={{ color: "#E2E8F0" }}
                contentStyle={{ background: "#0B0F17", border: "1px solid #334155", borderRadius: 6 }} formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
              {names.map((n) => <Bar key={n} dataKey={`${n}_${metric}`} name={n} fill={color(n)} radius={[3, 3, 0, 0]} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
}
